/**
 * Loftipop 3-tier upsell cart widget
 * ------------------------------------
 * Tier 1: free-shipping progress bar  (#UpsellBar, threshold from data-threshold, cents)
 * Tier 2: same-design siblings in other formats (Section Rendering API on the cart item's product)
 * Tier 3: the matching $4 sticker (gap-closer)
 *
 * Containers (#UpsellBar / #UpsellRecs) are siblings of the drawer's [data-products] node, so they
 * survive the theme replacing [data-products] on every cart change. A MutationObserver watches that
 * replacement and re-renders. See docs/upsell-widget-spec.md.
 */
(function () {
  'use strict';

  var BAR_ID = 'UpsellBar';
  var RECS_ID = 'UpsellRecs';
  var SCROLL_SEL = '#CartDrawer .drawer__scrollable';
  var MAX_TIER2 = 3;

  var observer = null;
  var debounceTimer = null;
  var rendering = false;

  /* ---------- helpers ---------- */

  function fmt(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      var f = (window.theme && theme.moneyFormat) || (window.Shopify && Shopify.money_format) || '${{amount}}';
      return Shopify.formatMoney(cents, f);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Coarse product "format" from its title, used to avoid offering a format already in the cart.
  function formatOf(title) {
    var t = (title || '').toLowerCase();
    if (t.indexOf('sticker') > -1) return 'sticker';
    if (t.indexOf('tote') > -1 || t.indexOf('bag') > -1) return 'tote';
    if (t.indexOf('sweatshirt') > -1 || t.indexOf('hoodie') > -1 || t.indexOf('crewneck') > -1) return 'sweatshirt';
    if (t.indexOf('tee') > -1 || t.indexOf('t-shirt') > -1 || t.indexOf('tshirt') > -1 || t.indexOf('shirt') > -1) return 'apparel';
    if (t.indexOf('travel mug') > -1 || t.indexOf('tumbler') > -1 || t.indexOf('insulated') > -1) return 'travel-mug';
    if (t.indexOf('mug') > -1) return 'mug';
    return '';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Extract the JSON array from a Section Rendering API response (it wraps output in a div).
  function parseArray(text) {
    try {
      var start = text.indexOf('[');
      var end = text.lastIndexOf(']');
      return (start > -1 && end > start) ? JSON.parse(text.slice(start, end + 1)) : [];
    } catch (e) { return []; }
  }

  // Push usable stickers from a Section-Rendering URL into `have` (up to max). Never throws.
  function topUpStickers(url, have, seen, inCart, max) {
    if (have.length >= max) return Promise.resolve(have);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        parseArray(text).forEach(function (s) {
          if (have.length < max && s.type === 'sticker' && s.available && !inCart[s.handle] && !seen[s.handle]) {
            seen[s.handle] = true;
            have.push(s);
          }
        });
        return have;
      })
      .catch(function () { return have; });
  }

  // Tier 3: matching-design sticker(s) first, then top up from Rachel's curated "bestselling
  // stickers" collection (manual sort preserved), then from Shopify sticker search as a final
  // fallback — so the slot never empties out even if the curated collection is blank/missing.
  function fillStickers(matching, inCart, max, poolHandle) {
    var have = matching.slice(0, max);
    if (have.length >= max) return Promise.resolve(have);
    var seen = {};
    have.forEach(function (s) { seen[s.handle] = true; });

    var chain = Promise.resolve(have);
    if (poolHandle) {
      chain = chain.then(function (h) {
        return topUpStickers('/collections/' + encodeURIComponent(poolHandle) + '?section_id=upsell-siblings', h, seen, inCart, max);
      });
    }
    return chain.then(function (h) {
      return topUpStickers('/search?q=sticker&type=product&section_id=upsell-siblings', h, seen, inCart, max);
    });
  }

  function getThreshold() {
    var bar = document.getElementById(BAR_ID);
    if (!bar) return 0;
    var v = parseInt(bar.getAttribute('data-threshold'), 10);
    return isNaN(v) ? 0 : v; // cents
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /* ---------- GA4 (attribution only; native pixel carries add_to_cart/purchase) ---------- */

  function track(eventName, items, listId) {
    try {
      var payload = {
        item_list_id: listId || 'upsell',
        items: (items || []).map(function (s) {
          return { item_id: s.handle, item_name: s.title, price: (s.price || 0) / 100, item_list_id: listId || 'upsell' };
        })
      };
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
      // If GA4 is ONLY the Shopify Customer Events pixel, wire analytics.publish() in a custom pixel (see spec §6).
    } catch (e) { /* never break the cart over analytics */ }
  }

  /* ---------- Tier 1: free-shipping bar ---------- */

  function renderBar(cart) {
    var bar = document.getElementById(BAR_ID);
    if (!bar) return;
    var threshold = getThreshold();

    if (bar.getAttribute('data-enabled') === 'false' || !threshold || cart.requires_shipping === false) {
      bar.hidden = true;
      bar.innerHTML = '';
      return;
    }

    var total = cart.total_price; // after cart-level discounts; matches Shopify's free-ship condition
    if (total >= threshold) {
      bar.innerHTML =
        '<div class="upsell-bar__track upsell-bar__track--done"><span class="upsell-bar__fill" style="width:100%"></span></div>' +
        '<p class="upsell-bar__msg">🎉 You’ve unlocked free shipping!</p>';
    } else {
      var pct = Math.max(2, Math.min(100, Math.round((total / threshold) * 100)));
      var remaining = threshold - total;
      bar.innerHTML =
        '<p class="upsell-bar__msg">You’re <strong>' + fmt(remaining) + '</strong> away from free shipping</p>' +
        '<div class="upsell-bar__track"><span class="upsell-bar__fill" style="width:' + pct + '%"></span></div>';
    }
    bar.hidden = false;
  }

  /* ---------- Tier 2 + 3: recommendations ---------- */

  function tileHTML(s, listId) {
    var img = s.image
      ? '<img class="upsell-tile__img" src="' + s.image + '" alt="" loading="lazy" width="60" height="60">'
      : '';
    var action;
    if (s.one_click) {
      action = '<button type="button" class="upsell-tile__add btn" data-variant="' + s.variant_id + '" data-handle="' + s.handle + '">Add</button>';
    } else {
      var variants = (s.variants || []).filter(function (v) { return v.available; });
      if (variants.length > 1) {
        // Inline variant dropdown so the customer adds without leaving the cart.
        var optName = (s.option_name || 'option').toLowerCase();
        var hint = variants.length + ' ' + optName + 's'; // e.g. "5 colors", "2 sizes"
        var opts = variants.map(function (v, i) {
          return '<option value="' + v.id + '"' + (i === 0 ? ' selected' : '') +
            ' data-price="' + escapeHtml(v.price_formatted || '') + '">' + v.title + '</option>';
        }).join('');
        action = '<span class="upsell-tile__opts">' + hint + '</span>' +
          '<select class="upsell-tile__select" aria-label="Choose ' + optName + '">' + opts + '</select>' +
          '<button type="button" class="upsell-tile__add btn" data-handle="' + s.handle + '">Add</button>';
      } else if (variants.length === 1) {
        // only one variant in stock → one-click, no need for a dropdown
        action = '<button type="button" class="upsell-tile__add btn" data-variant="' + variants[0].id + '" data-handle="' + s.handle + '">Add</button>';
      } else {
        action = '<a class="upsell-tile__add btn btn--secondary" href="' + s.url + '">View</a>';
      }
    }
    return (
      '<div class="upsell-tile" data-list="' + listId + '">' +
        '<a class="upsell-tile__link" href="' + s.url + '">' + img +
          '<span class="upsell-tile__title">' + s.title + '</span>' +
          '<span class="upsell-tile__price">' + (s.price_formatted || fmt(s.price)) + '</span>' +
        '</a>' +
        '<div class="upsell-tile__buy">' + action + '</div>' +
      '</div>'
    );
  }

  function renderRecs(cart) {
    var recs = document.getElementById(RECS_ID);
    if (!recs) return Promise.resolve();

    if (!cart.items || !cart.items.length) {
      recs.hidden = true;
      recs.innerHTML = '';
      return Promise.resolve();
    }

    // Config from theme settings (data attributes on #UpsellRecs).
    var cfg = {
      heading: recs.getAttribute('data-heading') || 'Treat yourself',
      showSiblings: recs.getAttribute('data-show-siblings') !== 'false',
      maxSiblings: parseInt(recs.getAttribute('data-max-siblings'), 10) || MAX_TIER2,
      showStickers: recs.getAttribute('data-show-stickers') !== 'false',
      stickerPool: recs.getAttribute('data-sticker-pool') || ''
    };
    // Always up to 6 tiles in a wrapped grid: 3 cols x 2 rows on desktop, 2 cols x 3 rows on
    // mobile, regardless of cart size (no empty cell, no horizontal scroll).
    var maxTotal = 6;

    // Highest-value cart line drives the recommendations.
    var target = cart.items.slice().sort(function (a, b) {
      return (b.final_line_price || b.line_price || 0) - (a.final_line_price || a.line_price || 0);
    })[0];

    return fetch('/products/' + target.handle + '?section_id=upsell-siblings', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var siblings = parseArray(text);

        var inCart = {};
        var cartFormats = {};
        cart.items.forEach(function (i) {
          inCart[i.handle] = true;
          var f = formatOf(i.title); if (f) cartFormats[f] = true;
        });

        var usable = siblings.filter(function (s) { return s.available && !inCart[s.handle]; });
        // Tier 2 (non-sticker) fill, in the priority order the section already emits:
        //   1) exact-design siblings in a format NOT already in the cart ("complete the set")
        //   2) thematically-related products (same theme collection) — any format allowed, so
        //      e.g. another bookish mug can show even though a mug is already in the cart.
        var cap = Math.min(cfg.maxSiblings, maxTotal);
        var tier2 = [];
        if (cfg.showSiblings) {
          usable.forEach(function (s) {
            if (tier2.length >= cap || s.type === 'sticker') return;
            // Design siblings avoid a format already in the cart; thematic items may repeat it.
            if (!s.thematic && cartFormats[formatOf(s.title)]) return;
            tier2.push(s);
          });
        }
        // Stickers fill the remaining slots up to the total.
        var stickerSlots = cfg.showStickers ? Math.max(0, maxTotal - tier2.length) : 0;
        var matchStickers = stickerSlots > 0 ? usable.filter(function (s) { return s.type === 'sticker'; }) : [];

        var tier3Promise = stickerSlots > 0
          ? fillStickers(matchStickers, inCart, stickerSlots, cfg.stickerPool)
          : Promise.resolve([]);

        return tier3Promise.then(function (tier3) {
          if (!tier2.length && !tier3.length) {
            recs.hidden = true;
            recs.innerHTML = '';
            return;
          }
          // Siblings first, then stickers, in one wrapped grid: 3x2 desktop / 2x3 mobile.
          var tiles = tier2.map(function (s) { return tileHTML(s, 'upsell_tier2'); })
            .concat(tier3.map(function (s) { return tileHTML(s, 'upsell_tier3'); }))
            .join('');
          recs.innerHTML = '<p class="upsell-recs__heading">' + escapeHtml(cfg.heading) + '</p>' +
            '<div class="upsell-recs__row upsell-recs__row--wrap">' + tiles + '</div>';
          recs.hidden = false;
          bindTiles(recs);
          track('view_item_list', tier2.concat(tier3), 'upsell_recs');
        });
      })
      .catch(function () {
        recs.hidden = true;
        recs.innerHTML = '';
      });
  }

  function bindTiles(root) {
    var buttons = root.querySelectorAll('button.upsell-tile__add');
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        var handle = btn.getAttribute('data-handle');
        var id = btn.getAttribute('data-variant');
        if (!id) {
          // dropdown tile: read the selected variant from the sibling <select>
          var sel = btn.parentNode.querySelector('.upsell-tile__select');
          if (sel) id = sel.value;
        }
        addToCart(id, handle, btn);
      });
    });

    // Update the displayed price when a variant is chosen.
    var selects = root.querySelectorAll('.upsell-tile__select');
    Array.prototype.forEach.call(selects, function (sel) {
      sel.addEventListener('change', function () {
        var opt = sel.options[sel.selectedIndex];
        var price = opt && opt.getAttribute('data-price');
        var tile = sel.closest('.upsell-tile');
        var priceEl = tile && tile.querySelector('.upsell-tile__price');
        if (price && priceEl) priceEl.textContent = price;
      });
    });
  }

  function addToCart(variantId, handle, btn) {
    if (!variantId) return;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    track('select_item', [{ handle: handle }], 'upsell_add');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function (product) { notifyAdded(variantId, product); })
      .catch(function () { if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); } });
  }

  // Trigger the theme's own post-add flow (rebuild drawer items + open the drawer) by dispatching
  // the event its CartDrawer listens for (theme.js:4389). We fire it from a hidden form that carries
  // a [name="id"] input, because another listener (theme.js:4147) reads event.target's id input and
  // would throw if we dispatched on bare document. Our MutationObserver then re-renders the widget.
  function notifyAdded(variantId, product) {
    try {
      var f = document.createElement('form');
      f.style.display = 'none';
      f.innerHTML = '<input name="id" value="' + variantId + '">';
      document.body.appendChild(f);
      f.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: product } }));
      document.body.removeChild(f);
    } catch (e) { /* widget still re-renders below */ }
    scheduleRender();
  }

  /* ---------- orchestration + observer ---------- */

  function render() {
    if (rendering) return;
    rendering = true;
    var hadObserver = !!observer;
    if (hadObserver) observer.disconnect(); // don't observe our own writes

    getCart()
      .then(function (cart) {
        renderBar(cart);
        return renderRecs(cart);
      })
      .catch(function () { /* leave containers hidden */ })
      .then(function () {
        rendering = false;
        if (hadObserver) connectObserver();
      });
  }

  function scheduleRender() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(render, 50);
  }

  function connectObserver() {
    var scrollable = document.querySelector(SCROLL_SEL);
    if (!scrollable) return;
    if (!observer) {
      observer = new MutationObserver(function (mutations) {
        // Ignore mutations confined to our own widget nodes.
        var relevant = mutations.some(function (m) {
          return !(m.target && m.target.closest && m.target.closest('[data-upsell]'));
        });
        if (relevant) scheduleRender();
      });
    }
    observer.observe(scrollable, { childList: true, subtree: true });
  }

  function init() {
    if (!document.getElementById(BAR_ID) && !document.getElementById(RECS_ID)) return;
    connectObserver();
    render(); // drawer may already hold items
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
